import { FC, useCallback, useMemo, useState } from 'react';
import { MRT_Row, MRT_RowData } from 'material-react-table';

import { LocalTime, WeightChargeDto } from 'src/generated/services/TFinancialApi';
import { useUpsertWeightCharge } from 'src/use-cases/serviceProvider/useServiceProvider';
import { formatNumber } from 'src/utils/utility';
import { formatTimeDisplay } from 'src/utils/date';
import { format, parseISO } from 'date-fns';
import { ContractManagerColumnHeader } from '../ContractManagerColumnHeader';
import { RateCardChargesTableBase } from './TableBase';
import { getTableColumns } from './utils';
import { ITableProps } from './types';
import { CALCULATION_METHODS, CHARGE_UNIT } from './constants';
import { applyFiltersAndSorting, createFilterOptions, commonBooleanAccessors } from './filterUtils';
import { createTableHeader } from 'src/presentations/components/molecules/table/tableHeader/helpers';
import { DatePickerEditCell, TimePickerEditCell } from './PickerEditCells';

// Shared helper function to format number for cell display
const getCellFormatNumber = (row: MRT_Row<MRT_RowData>, key: string) => {
  const value = row.original[key];
  return value !== null && value !== undefined && value !== '' ? formatNumber(value) : '-';
};

// Helper function to parse LocalTime object or string to Date object for TimePicker
const parseTimeToDate = (time: string | LocalTime | null | undefined): Date | null => {
  if (!time) return null;
  // Handle LocalTime object format
  if (typeof time === 'object' && 'hour' in time) {
    const date = new Date();
    date.setHours(time.hour ?? 0, time.minute ?? 0, time.second || 0, 0);
    return date;
  }
  // Handle string format (HH:mm:ss)
  if (typeof time === 'string') {
    const parts = time.split(':').map(Number);
    const date = new Date();
    date.setHours(parts[0] || 0, parts[1] || 0, parts[2] || 0, 0);
    return date;
  }
  return null;
};

export const RateCardChargesTableWeight: FC<ITableProps> = (props) => {
  const { contractFlow, chargeCategory, tableRef, filters, setFilters, sorting, setSorting, isAdmin } = props;
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  // Original unfiltered data
  const originalData = useMemo(() => contractFlow?.[chargeCategory] ?? [], [contractFlow, chargeCategory]);

  // Define accessors for boolean fields and time fields
  const accessors = useMemo(
    () => ({
      ...commonBooleanAccessors,
      startTime: (item: any) => formatTimeDisplay(item.startTime),
      endTime: (item: any) => formatTimeDisplay(item.endTime),
    }),
    []
  );

  // Apply filters with accessors
  const tableData = useMemo(() => applyFiltersAndSorting(originalData, filters, accessors, sorting), [originalData, filters, accessors, sorting]);

  // Handle sorting and filtering
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      setSorting([{ id: columnId, desc: direction === 'desc' }]);
    },
    [setSorting]
  );

  // Handle filter changes
  const handleFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      setFilters((prev) => ({
        ...prev,
        [columnId]: selectedValues,
      }));
    },
    [setFilters]
  );

  const columns = useMemo(() => {
    const weightColumns = [
      {
        accessorKey: 'minimumPrice',
        header: 'MINIMUM PRICE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='minimumPrice'
            originalData={originalData}
            currentFilters={filters.minimumPrice || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => (
          <>
            {row?.original.minimumPrice || parseFloat(row?.original.minimumPrice.toString()) === 0
              ? `${formatNumber(parseFloat(row?.original.minimumPrice?.toString()))}`
              : '-'}
          </>
        ),
      },
      {
        accessorKey: 'unitPrice',
        header: 'UNIT PRICE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='unitPrice'
            originalData={originalData}
            currentFilters={filters.unitPrice || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => (
          <>
            {row?.original.unitPrice || parseFloat(row?.original.unitPrice.toString()) === 0
              ? `${formatNumber(parseFloat(row?.original.unitPrice?.toString()))}`
              : '-'}
          </>
        ),
      },
      {
        id: 'currency',
        accessorKey: 'currency',
        header: 'CURRENCY',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='currency'
            originalData={originalData}
            currentFilters={filters.currency || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.currency ? row.original.currency : '-'}</>,
      },
      {
        accessorKey: 'chargeUnit',
        header: 'CHARGE UNIT',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='chargeUnit'
            originalData={originalData}
            currentFilters={filters.chargeUnit || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        editVariant: 'select',
        Cell: ({ row }) => <>{row?.original.chargeUnit ? row.original.chargeUnit : '-'}</>,
        editSelectOptions: CHARGE_UNIT,
        muiEditTextFieldProps: {
          select: true,
        },
      },
      {
        accessorKey: 'calculationMethod',
        header: 'CALCULATION METHOD',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='calculationMethod'
            originalData={originalData}
            currentFilters={filters.calculationMethod || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        editVariant: 'select',
        editSelectOptions: CALCULATION_METHODS,
        muiEditTextFieldProps: {
          select: true,
        },
      },
      {
        accessorKey: 'minimumChargeableWeight',
        header: 'MINIMUM RANGE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='minimumChargeableWeight'
            originalData={originalData}
            currentFilters={filters.minimumChargeableWeight || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => getCellFormatNumber(row, 'minimumChargeableWeight'),
      },
      {
        accessorKey: 'maximumChargeableWeight',
        header: 'MAXIMUM RANGE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='maximumChargeableWeight'
            originalData={originalData}
            currentFilters={filters.maximumChargeableWeight || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => getCellFormatNumber(row, 'maximumChargeableWeight'),
      },
      {
        accessorKey: 'includedQuantity',
        header: 'INCLUDED QUANTITY',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='includedQuantity'
            originalData={originalData}
            currentFilters={filters.includedQuantity || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => getCellFormatNumber(row, 'includedQuantity'),
      },
      {
        accessorKey: 'startDate',
        header: 'VALIDITY START DATE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='startDate'
            originalData={originalData}
            currentFilters={filters.startDate || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.startDate ? format(parseISO(row.original.startDate), 'dd-MMM-yyyy') : '-'}</>,
        Edit: ({ column, row, table }) => <DatePickerEditCell column={column} row={row} table={table} fieldKey='startDate' />,
      },
      {
        accessorKey: 'endDate',
        header: 'VALIDITY END DATE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='endDate'
            originalData={originalData}
            currentFilters={filters.endDate || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.endDate ? format(parseISO(row.original.endDate), 'dd-MMM-yyyy') : '-'}</>,
        Edit: ({ column, row, table }) => <DatePickerEditCell column={column} row={row} table={table} fieldKey='endDate' />,
      },
      {
        accessorKey: 'startTime',
        header: 'START TIME',
        enableSorting: false,
        minSize: 200,
        size: 200,
        Header: ({ column }) =>
          createTableHeader(column, handleTableSort, {
            onFilter: handleFilter,
            filterValues: createFilterOptions(originalData, 'startTime', (item) => formatTimeDisplay(item.startTime)),
            initialSelectedValues: filters.startTime || [],
          }),
        Cell: ({ row }) => <>{formatTimeDisplay(row?.original.startTime)}</>,
        Edit: ({ column, row, table }) => <TimePickerEditCell column={column} row={row} table={table} fieldKey='startTime' parseTimeToDate={parseTimeToDate} />,
      },
      {
        accessorKey: 'endTime',
        header: 'END TIME',
        enableSorting: false,
        minSize: 200,
        size: 200,
        Header: ({ column }) =>
          createTableHeader(column, handleTableSort, {
            onFilter: handleFilter,
            filterValues: createFilterOptions(originalData, 'endTime', (item) => formatTimeDisplay(item.endTime)),
            initialSelectedValues: filters.endTime || [],
          }),
        Cell: ({ row }) => <>{formatTimeDisplay(row?.original.endTime)}</>,
        Edit: ({ column, row, table }) => <TimePickerEditCell column={column} row={row} table={table} fieldKey='endTime' parseTimeToDate={parseTimeToDate} />,
      },
    ];

    const allColumns = getTableColumns(weightColumns, validationErrors, setValidationErrors, {
      filters,
      handleFilter,
      handleSort: handleTableSort,
      originalData,
      createFilterOptions,
    });

    return allColumns;
  }, [validationErrors, filters, originalData]);

  const { mutateAsync: upsertWeightCharge } = useUpsertWeightCharge();

  const onValidateCharge = useCallback((charge: Record<string, any>) => {
    const validationResult = {
      chargeName: !charge.chargeName || charge.chargeName === '' ? 'Required' : '',
      chargeCategory: !charge.chargeCategory || charge.chargeCategory === '' ? 'Required' : '',
      legType: !charge.legType || charge.legType === '' ? 'Required' : '',
      unitPrice: (!charge.unitPrice || charge.unitPrice === '') && charge.unitPrice !== 0 ? 'Required' : '',
      currency: !charge.currency || charge.currency === '' ? 'Required' : '',
    };

    return validationResult;
  }, []);

  const onUpsertCharge = useCallback(
    async (row: MRT_Row<MRT_RowData>, values: Record<string, any>) => {
      // Format time value to string "HH:mm:ss" for backend LocalTime
      const formatTimeValue = (timeValue: string | LocalTime | Date | null | undefined): string | null => {
        if (!timeValue) return null;
        // If it's already a string (HH:mm:ss format)
        if (typeof timeValue === 'string') {
          return timeValue;
        }
        // If it's a LocalTime object (from existing data)
        if (typeof timeValue === 'object' && 'hour' in timeValue) {
          const hour = String(timeValue.hour).padStart(2, '0');
          const minute = String(timeValue.minute).padStart(2, '0');
          const second = String(timeValue.second || 0).padStart(2, '0');
          return `${hour}:${minute}:${second}`;
        }
        // If it's a Date object (from TimePicker), extract time parts
        if (timeValue instanceof Date) {
          const hour = String(timeValue.getHours()).padStart(2, '0');
          const minute = String(timeValue.getMinutes()).padStart(2, '0');
          const second = String(timeValue.getSeconds()).padStart(2, '0');
          return `${hour}:${minute}:${second}`;
        }
        return null;
      };

      const reqBody: WeightChargeDto & { contractFlowId: string; deviationExcluded?: boolean } = {
        id: row.original?.id || null,
        contractFlowId: contractFlow?.id,
        legType: values.legType,
        chargeName: values.chargeName,
        chargeCategory: values.chargeCategory,
        accessorial: values.accessorial.toLowerCase() === 'true',
        approval: values.approval.toLowerCase() === 'true',
        outlay: values.outlay.toLowerCase() === 'true',
        deviationExcluded: values.deviationExcluded ? values.deviationExcluded.toLowerCase() === 'true' : false,
        unitPrice: values.unitPrice,
        currency: values.currency || null,
        chargeUnit: values.chargeUnit || null,
        minimumPrice: values.minimumPrice || 0,
        minimumChargeableWeight: values.minimumChargeableWeight || null,
        maximumChargeableWeight: values.maximumChargeableWeight || null,
        includedQuantity: values.includedQuantity ? Number(values.includedQuantity) : null,
        calculationMethod: values.calculationMethod || null,
        startDate: values?.startDate ? format(new Date(values.startDate), 'yyyy-MM-dd') : null,
        endDate: values?.endDate ? format(new Date(values.endDate), 'yyyy-MM-dd') : null,
        // Backend expects LocalTime as string "HH:mm:ss", not as object
        startTime: formatTimeValue(values?.startTime) as unknown as LocalTime,
        endTime: formatTimeValue(values?.endTime) as unknown as LocalTime,
      };

      await upsertWeightCharge(reqBody);
    },
    [upsertWeightCharge, contractFlow]
  );

  return (
    <RateCardChargesTableBase
      tableRef={tableRef}
      tableData={tableData}
      columns={columns}
      sorting={sorting}
      contractFlow={contractFlow}
      chargeCategory={chargeCategory}
      onUpsertCharge={onUpsertCharge}
      setValidationErrors={setValidationErrors}
      onValidateCharge={onValidateCharge}
      isAdmin={isAdmin}
    />
  );
};
